import type { Address } from '@this+that/models';

import { isSupersededSnapshot, resolveSnapshotLineageId } from './superseded';
import { HandlerButton } from '../../hooks/UserCommunications/useViewMessageComposerHandler';
import type { ComposeSnapshot } from '../../models/composeSnapshot';
import {
  type DraftData,
  hasDraftWorthyContent,
} from '../../pages/private/Inbox/components/hooks/useSmartDraftManager';
import {
  COMPOSE_SNAPSHOT_ORPHAN_GRACE_MS,
  COMPOSE_SNAPSHOT_SYNC_MAX_ATTEMPTS,
} from '../constants/drafts';
import { hasPendingDraftWrite } from '../draftWrites';

export interface SyncBookkeeping {
  attemptsBySessionId: ReadonlyMap<string, number>;
  retryNotBeforeBySessionId: ReadonlyMap<string, number>;
}

export interface SyncEligibilityContext extends SyncBookkeeping {
  liveSessionIds: Record<string, true>;
  now: number;
}

const isReplyHandler = (handler?: HandlerButton): boolean =>
  handler === HandlerButton.REPLY || handler === HandlerButton.REPLY_ALL;

const isInheritingSubject = (handler?: HandlerButton): boolean =>
  isReplyHandler(handler) || handler === HandlerButton.FORWARD;

const toWorthinessData = (snapshot: ComposeSnapshot): DraftData => ({
  subject: snapshot.subject,
  message: snapshot.message,
  modifiedQuotedContent: snapshot.modifiedQuotedContent,
  toAddresses: snapshot.toAddresses as Address[],
  ccAddresses: snapshot.ccAddresses,
  bccAddresses: snapshot.bccAddresses,
});

const isDraftWorthy = (snapshot: ComposeSnapshot): boolean =>
  snapshot.attachments.length > 0 ||
  hasDraftWorthyContent(toWorthinessData(snapshot), {
    countsRecipients: !isReplyHandler(snapshot.handler),
    countsSubject: !isInheritingSubject(snapshot.handler),
  });

// An update without a known server version could overwrite an edit made elsewhere.
const hasSafeWriteTarget = ({
  draftId,
  draftVersion,
}: ComposeSnapshot): boolean => !draftId || Boolean(draftVersion);

const hasPendingWrite = (snapshot: ComposeSnapshot): boolean =>
  hasPendingDraftWrite({
    draftId: snapshot.draftId,
    lineageId: resolveSnapshotLineageId(snapshot),
  });

export const isSnapshotSyncEligible = (
  snapshot: ComposeSnapshot,
  context: SyncEligibilityContext,
): boolean =>
  !context.liveSessionIds[snapshot.sessionId] &&
  !snapshot.syncBlockedReason &&
  !snapshot.attachmentsTruncated &&
  hasSafeWriteTarget(snapshot) &&
  !hasPendingWrite(snapshot) &&
  (context.attemptsBySessionId.get(snapshot.sessionId) ?? 0) <
    COMPOSE_SNAPSHOT_SYNC_MAX_ATTEMPTS &&
  context.now >=
    (context.retryNotBeforeBySessionId.get(snapshot.sessionId) ?? 0) &&
  // A compose live in another tab keeps rewriting its snapshot; only idle ones are fair game.
  context.now - snapshot.updatedAt >= COMPOSE_SNAPSHOT_ORPHAN_GRACE_MS &&
  isDraftWorthy(snapshot);

export const pickNextSnapshot = (
  snapshots: ComposeSnapshot[],
  context: SyncEligibilityContext,
): ComposeSnapshot | undefined =>
  snapshots
    .filter(
      (snapshot) =>
        isSnapshotSyncEligible(snapshot, context) &&
        !isSupersededSnapshot(snapshot, snapshots),
    )
    .sort((a, b) => a.updatedAt - b.updatedAt)[0];
